/**
 * Building shell — the parameters chosen ONCE and reused on every floor so the
 * architecture lines up across stories: footprint (w×h), the stairwell/elevator
 * CORE rect (a fixed position → the stairs tile is aligned on every floor), and
 * the window column/row positions (applied to above-ground floors only).
 *
 * Subdivision runs AROUND the core: the plate minus the core decomposes into up
 * to four rectangular strips (left/right full-height, top/bottom over the core's
 * columns), each BSP-subdivided. Core + strip-cells tile the plate sharing 1-tile
 * walls — same gap-free guarantee as a plain BSP, but with a reserved core.
 *
 * Pure + deterministic.
 */

import { CONFIG } from '../../config';
import type { BuildProfile } from '../establishment';
import { bspSubdivide, floorH, floorW, type Rect } from './bsp';

export interface Shell {
  w: number;
  h: number;
  /** Stairwell/elevator core (outer rect within the plate), same on every floor. */
  core: Rect;
  /** Exterior window columns (x) and rows (y), aligned across above-ground floors. */
  windowCols: number[];
  windowRows: number[];
}

function randInt(rng: () => number, range: readonly [number, number]): number {
  const [lo, hi] = range;
  return lo + Math.floor(rng() * (hi - lo + 1));
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Choose the shell for a building (footprint, centred core, window lines). */
export function pickShell(profile: BuildProfile, rng: () => number): Shell {
  const w = randInt(rng, profile.floorW);
  const h = randInt(rng, profile.floorH);
  const cw = clamp(Math.round(w * 0.22), 6, 10);
  const ch = clamp(Math.round(h * 0.28), 6, 9);
  const cx0 = Math.floor((w - cw) / 2);
  const cy0 = Math.floor((h - ch) / 2);
  const core: Rect = { x0: cx0, y0: cy0, x1: cx0 + cw - 1, y1: cy0 + ch - 1 };

  const windowCols: number[] = [];
  for (let x = profile.windowSpacing; x <= w - 2; x += profile.windowSpacing) windowCols.push(x);
  const windowRows: number[] = [];
  for (let y = profile.windowSpacing; y <= h - 2; y += profile.windowSpacing) windowRows.push(y);

  return { w, h, core, windowCols, windowRows };
}

/**
 * The stair PAIR at the core's centre: up and down side by side, aligned on
 * every floor because the core is. Direction is a place rather than a keypress,
 * so travelling up lands you on the destination floor's DOWN tile — you arrive
 * beside the way back, and never on the tile that would send you straight on.
 */
export function coreStairTiles(shell: Shell): {
  up: { x: number; y: number };
  down: { x: number; y: number };
} {
  const cx = Math.round((shell.core.x0 + shell.core.x1) / 2);
  const cy = Math.round((shell.core.y0 + shell.core.y1) / 2);
  return { up: { x: cx - 1, y: cy }, down: { x: cx, y: cy } };
}

/** The core's centre — the arrival/reference tile the shell is built around. */
export function coreTile(shell: Shell): { x: number; y: number } {
  return {
    x: Math.round((shell.core.x0 + shell.core.x1) / 2),
    y: Math.round((shell.core.y0 + shell.core.y1) / 2),
  };
}

/**
 * The service RISER bank: tiles inside the core, on its interior ring, in a
 * fixed order. Risers share the building core with the stairs in real
 * architecture, and the core is the one rect that's identical on every floor —
 * so taking riser positions from the shell makes them aligned by construction
 * (the same tap sits at the same (x,y) on every story). The stairs tile at the
 * core centre is never one of them.
 */
export function riserTiles(shell: Shell): { x: number; y: number }[] {
  const f = { x0: shell.core.x0 + 1, y0: shell.core.y0 + 1, x1: shell.core.x1 - 1, y1: shell.core.y1 - 1 };
  const stairs = coreStairTiles(shell);
  const out: { x: number; y: number }[] = [];
  // Clockwise from the top-left of the interior ring: deterministic, and spreads
  // consecutive services around the shaft rather than stacking them in a corner.
  for (let x = f.x0; x <= f.x1; x++) out.push({ x, y: f.y0 });
  for (let y = f.y0 + 1; y <= f.y1; y++) out.push({ x: f.x1, y });
  for (let x = f.x1 - 1; x >= f.x0; x--) out.push({ x, y: f.y1 });
  for (let y = f.y1 - 1; y > f.y0; y--) out.push({ x: f.x0, y });
  // Neither stair tile may be taken by a riser tap.
  return out.filter((t) =>
    !(t.x === stairs.up.x && t.y === stairs.up.y) && !(t.x === stairs.down.x && t.y === stairs.down.y));
}

/** BSP a strip only if it's a real (non-degenerate) rectangle; else drop it. */
function bspStrip(r: Rect, rng: () => number, axis: 'both' | 'x' | 'y' = 'both'): Rect[] {
  if (floorW(r) < 1 || floorH(r) < 1) return [];
  return bspSubdivide(r, rng, axis);
}

/** The cells of one floor plate, plus which of them are circulation. */
export interface Plate {
  /** Cells tiling the plate, sharing 1-tile walls. Index 0 is always the core. */
  cells: Rect[];
  /** Indices of every circulation cell — the ring around the core plus the spurs
   *  that branch off it into the outer strips. Empty when the plate was too
   *  small for a ring. */
  corridors: number[];
  /** The subset of `corridors` forming the RING itself. Their mutual walls are
   *  opened into archways (a continuous loop); spurs join the ring by a door. */
  ring: number[];
  /**
   * The rect each cell registers as a ROOM. Same as the cell everywhere except
   * the left/right corridor segments, which are stretched one tile into the
   * junction rows they own — so the tiles opened at the ring's corners still lie
   * inside some room's interior (no dead space) while interiors stay disjoint.
   */
  roomRects: Rect[];
}

/**
 * The RACETRACK: two corridor arms running the plate's full height either side
 * of the core, closed at the top and bottom by segments across the core. Arms
 * that run wall-to-wall are the whole point — a collar hugging the core only
 * serves the rooms next to it, while a full-height arm gives every room in the
 * strip beside it corridor frontage.
 *
 * Order is [left arm, right arm, top segment, bottom segment]; they tile the
 * area between the room strips, sharing 1-tile walls with each other and with
 * the rooms. `h` is the plate height.
 */
function ringCells(core: Rect, ring: Rect, h: number): Rect[] {
  return [
    { x0: ring.x0, y0: 0, x1: core.x0, y1: h - 1 },              // left arm
    { x0: core.x1, y0: 0, x1: ring.x1, y1: h - 1 },              // right arm
    { x0: core.x0, y0: ring.y0, x1: core.x1, y1: core.y0 },      // top, between arms
    { x0: core.x0, y0: core.y1, x1: core.x1, y1: ring.y1 },      // bottom, between arms
  ];
}

/** Would a ring `cw` tiles wide still leave the plate room for rooms outside it?
 *  Each outer strip needs at least a 1-tile-deep interior, i.e. outer size ≥ 3. */
function ringFits(shell: Shell, cw: number): Rect | null {
  const { w, h, core } = shell;
  const pad = cw + 1; // corridor floor + the wall it shares with the outer rooms
  const ring: Rect = {
    x0: core.x0 - pad, y0: core.y0 - pad, x1: core.x1 + pad, y1: core.y1 + pad,
  };
  const ok = ring.x0 >= 2 && ring.y0 >= 2 && ring.x1 <= w - 3 && ring.y1 <= h - 3;
  return ok ? ring : null;
}

/**
 * All cells for a floor: the reserved core, the CORRIDOR ring around it, and the
 * BSP-subdivided strips outside the ring. Index 0 is always the core; cells tile
 * the plate sharing 1-tile walls.
 *
 * This is the real circulation model (plan: "corridors are thin rooms that other
 * rooms door onto, threading back to the core"): the ring wraps the stair core,
 * so every room's door opens onto circulation that leads to the stairs, instead
 * of rooms opening into each other like a warren. A plate too small to fit a
 * ring AND rooms outside it falls back to the old core-only subdivision.
 */
export function subdivideAround(shell: Shell, rng: () => number): Plate {
  const { w, h, core } = shell;
  const ringRect = ringFits(shell, CONFIG.gen.corridorWidth) ?? ringFits(shell, 1);
  const cw = ringRect ? ringRect.x1 - core.x1 - 1 : 0;
  const inner = ringRect ?? core; // what the outer strips wrap around
  const ringCellList = ringRect ? ringCells(core, ringRect, h) : [];

  const cells: Rect[] = [core, ...ringCellList];
  const ring = ringCellList.map((_c, i) => i + 1);
  const corridors = ring.slice();

  const { minRoom } = CONFIG.gen.bsp;
  const pushCorridor = (r: Rect) => { corridors.push(cells.length); cells.push(r); };

  /**
   * Subdivide one outer strip into rooms served by circulation.
   *
   * `side` is the edge of the strip that faces the ring. A strip deep enough for
   * two room bands gets a LANE running its full length, parallel to the ring,
   * with a SPUR joining that lane back to the ring — so both bands are single-
   * loaded (rooms line the corridor on either side, the way a real office floor
   * works) instead of BSP piling rooms behind rooms. A shallow strip gets the
   * spur alone; a tiny one is left as plain rooms.
   *
   * Depth is measured from the ring edge outward, length runs along the ring.
   */
  const addStrip = (strip: Rect, side: 'left' | 'right' | 'top' | 'bottom') => {
    if (!ringRect) { cells.push(...bspStrip(strip, rng)); return; }
    const vertical = side === 'left' || side === 'right'; // lane runs vertically
    // Rooms stack ALONG the corridor, never behind each other: a back room would
    // have no corridor wall to open onto.
    const band = vertical ? 'y' : 'x';
    const depth = vertical ? strip.x1 - strip.x0 + 1 : strip.y1 - strip.y0 + 1;
    const [lo, hi] = vertical ? [strip.y0, strip.y1] : [strip.x0, strip.x1];
    // The spur is centred on the core so it meets the ring's mid-segment.
    const mid = vertical
      ? Math.round((core.y0 + core.y1) / 2)
      : Math.round((core.x0 + core.x1) / 2);
    const s0 = mid - Math.floor((cw + 1) / 2);
    const s1 = s0 + cw + 1;
    const spurFits = s0 - lo >= 2 && hi - s1 >= 2;
    // Room for a lane plus a band of rooms on each side of it?
    const laneFits = depth >= 2 * (minRoom + 2) + cw;

    /** Slice the strip perpendicular to the lane, keeping `side`'s half. */
    const near = (r: Rect, cut: number): Rect =>
      side === 'left' ? { ...r, x0: cut }
        : side === 'right' ? { ...r, x1: cut }
        : side === 'top' ? { ...r, y0: cut }
        : { ...r, y1: cut };
    const far = (r: Rect, cut: number): Rect =>
      side === 'left' ? { ...r, x1: cut }
        : side === 'right' ? { ...r, x0: cut }
        : side === 'top' ? { ...r, y1: cut }
        : { ...r, y0: cut };

    if (!laneFits) {
      // Shallow strip: one band, split by the spur that reaches the ring.
      if (!spurFits) { cells.push(...bspStrip(strip, rng, band)); return; }
      const spur: Rect = vertical
        ? { x0: strip.x0, y0: s0, x1: strip.x1, y1: s1 }
        : { x0: s0, y0: strip.y0, x1: s1, y1: strip.y1 };
      cells.push(...bspStrip(vertical ? { ...strip, y1: s0 } : { ...strip, x1: s0 }, rng, band));
      pushCorridor(spur);
      cells.push(...bspStrip(vertical ? { ...strip, y0: s1 } : { ...strip, x0: s1 }, rng, band));
      return;
    }

    // Deep strip: lane at mid-depth, spur from the ring to the lane.
    const ringEdge = side === 'left' ? strip.x1 : side === 'right' ? strip.x0
      : side === 'top' ? strip.y1 : strip.y0;
    const inward = side === 'left' || side === 'top' ? -1 : 1; // toward the plate edge
    const laneNear = ringEdge + inward * Math.round((depth - cw) / 2);
    const laneFar = laneNear + inward * (cw + 1);
    const lane: Rect = vertical
      ? { x0: Math.min(laneNear, laneFar), y0: strip.y0, x1: Math.max(laneNear, laneFar), y1: strip.y1 }
      : { x0: strip.x0, y0: Math.min(laneNear, laneFar), x1: strip.x1, y1: Math.max(laneNear, laneFar) };
    const nearBand = near(strip, laneNear);
    const farBand = far(strip, laneFar);
    if (spurFits) {
      const spur: Rect = vertical
        ? { x0: nearBand.x0, y0: s0, x1: nearBand.x1, y1: s1 }
        : { x0: s0, y0: nearBand.y0, x1: s1, y1: nearBand.y1 };
      cells.push(...bspStrip(vertical ? { ...nearBand, y1: s0 } : { ...nearBand, x1: s0 }, rng, band));
      pushCorridor(spur);
      cells.push(...bspStrip(vertical ? { ...nearBand, y0: s1 } : { ...nearBand, x0: s1 }, rng, band));
    } else {
      cells.push(...bspStrip(nearBand, rng, band));
    }
    pushCorridor(lane);
    cells.push(...bspStrip(farBand, rng, band));
  };

  // The room strips: one outside each arm (full height), and one above/below the
  // top/bottom segments (between the arms). Every strip has a corridor along its
  // whole inner edge.
  if (ringRect) {
    addStrip({ x0: 0, y0: 0, x1: ringRect.x0, y1: h - 1 }, 'left');
    addStrip({ x0: ringRect.x1, y0: 0, x1: w - 1, y1: h - 1 }, 'right');
    addStrip({ x0: core.x0, y0: 0, x1: core.x1, y1: ringRect.y0 }, 'top');
    addStrip({ x0: core.x0, y0: ringRect.y1, x1: core.x1, y1: h - 1 }, 'bottom');
  } else {
    // No room for a racetrack: fall back to plain strips around the core.
    cells.push(...bspStrip({ x0: 0, y0: 0, x1: inner.x0, y1: h - 1 }, rng));
    cells.push(...bspStrip({ x0: inner.x1, y0: 0, x1: w - 1, y1: h - 1 }, rng));
    cells.push(...bspStrip({ x0: inner.x0, y0: 0, x1: inner.x1, y1: inner.y0 }, rng));
    cells.push(...bspStrip({ x0: inner.x0, y0: inner.y1, x1: inner.x1, y1: h - 1 }, rng));
  }

  const roomRects = cells.slice();
  if (ringRect) {
    // Ring order is [left arm, right arm, top, bottom] → cells 1..4. The top and
    // bottom segments own the junction columns where they meet the arms, so the
    // tiles opened there still lie inside a room.
    roomRects[3] = { x0: core.x0 - 1, y0: ringRect.y0, x1: core.x1 + 1, y1: core.y0 };
    roomRects[4] = { x0: core.x0 - 1, y0: core.y1, x1: core.x1 + 1, y1: ringRect.y1 };
  }
  return { cells, corridors, ring, roomRects };
}

/** Re-export for callers that only need the config-driven bounds. */
export const MIN_ROOM = CONFIG.gen.bsp.minRoom;
